import type { PlanCatalogItem } from "@/domain/types/planCatalog";
import type { TenantGridDTO } from "@/domain/repositories/ITenantRepository";
import type { EcosystemUsageReportDTO, SaasMetricsDTO } from "@/domain/repositories/IReportRepository";
import { clamp } from "./format";
import type {
  Clinic,
  ConnectionPack,
  CostItem,
  MarketingPack,
  MixWeight,
  ProjectionModel,
} from "./types";

export type LiveImportSummary = {
  clinics: number;
  pctAvanzado: number;
  pctConexion: number;
  pctMarketing: number;
  recurrentes: number;
  connectionPacks: number;
};

type LiveRecurrente = {
  id: number;
  nombre: string;
  categoria: string;
  monto: number;
  activo: boolean;
};

type LiveAdicional = {
  id: number;
  nombre: string;
  tipo: string;
  precioUnitario: number;
  cupoMensajes: number | null;
  activo: boolean;
};

export type LiveMetricsSnapshot = {
  plans: PlanCatalogItem[];
  tenants: TenantGridDTO[];
  usage: EcosystemUsageReportDTO | null;
  saas: SaasMetricsDTO | null;
  adicionales: LiveAdicional[];
  recurrentes: LiveRecurrente[];
};

function asNumber(value: unknown, fallback = 0): number {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function closestPack<T extends { quota: number }>(quota: number, packs: T[]): T | null {
  if (packs.length === 0) return null;
  return packs.reduce((best, pack) =>
    Math.abs(pack.quota - quota) < Math.abs(best.quota - quota) ? pack : best,
  );
}

function priceAt(
  plans: PlanCatalogItem[],
  tier: PlanCatalogItem["productTier"],
  professionals: number,
  consultorios: number,
): number | null {
  const match = plans
    .filter((plan) => plan.planActivo && plan.productTier === tier
      && plan.profesionales === professionals && plan.consultorios === consultorios)
    .sort((a, b) => a.precio - b.precio)[0];
  return match ? match.precio : null;
}

function cheapestTierPrice(plans: PlanCatalogItem[], tier: PlanCatalogItem["productTier"]): number | null {
  const match = plans
    .filter((plan) => plan.planActivo && plan.productTier === tier)
    .sort((a, b) => a.precio - b.precio || a.profesionales - b.profesionales)[0];
  return match ? match.precio : null;
}

function mixFromCounts(counts: Map<string, number>): MixWeight[] {
  const total = [...counts.values()].reduce((sum, value) => sum + value, 0);
  if (total <= 0) return [];
  return [...counts.entries()].map(([packId, count]) => ({
    packId,
    weight: count / total,
  }));
}

export function applyLiveMetrics(
  model: ProjectionModel,
  snapshot: LiveMetricsSnapshot,
): { model: ProjectionModel; summary: LiveImportSummary } {
  const plans = snapshot.plans.map((plan) => ({
    ...plan,
    productTier: plan.productTier ?? "BASE",
    planActivo: plan.planActivo !== false,
  }));
  const planById = new Map(plans.map((plan) => [plan.id, plan]));
  const usageByTenant = new Map(
    (snapshot.usage?.tenants ?? []).map((row) => [String(row.tenantId), row]),
  );

  const activeTenants = snapshot.tenants.filter((tenant) => tenant.activo);
  const clinics: Clinic[] = activeTenants.map((tenant) => {
    const plan = tenant.planAdminId != null ? planById.get(tenant.planAdminId) : undefined;
    const usage = usageByTenant.get(String(tenant.id));
    return {
      id: `live_${String(tenant.id)}`,
      name: tenant.nombre,
      professionals: Math.max(1, plan?.profesionales ?? 1),
      consultorios: Math.max(1, plan?.consultorios ?? 1),
      enabled: true,
      whatsappHint: Math.max(0, usage?.whatsappMessages ?? 0),
    };
  });

  const n = clinics.length;
  const avanzadoCount = activeTenants.filter((tenant) => {
    const plan = tenant.planAdminId != null ? planById.get(tenant.planAdminId) : undefined;
    return plan?.productTier === "AVANZADO";
  }).length;
  const conexionTenants = activeTenants.filter((tenant) => {
    const usage = usageByTenant.get(String(tenant.id));
    return (usage?.whatsappMonthlyLimit ?? 0) > 0;
  });
  const marketingTenants = activeTenants.filter((tenant) => {
    const usage = usageByTenant.get(String(tenant.id));
    return (usage?.featuresEnabled ?? []).includes("marketing.email") || (usage?.emailMessages ?? 0) > 0;
  });

  const conexionAdicionales = snapshot.adicionales.filter(
    (item) => item.activo && item.tipo === "COOFIT_CONEXION" && (item.cupoMensajes ?? 0) > 0,
  );
  const marketingAdicionales = snapshot.adicionales.filter(
    (item) => item.activo && item.tipo === "EMAIL_MENSAJES" && (item.cupoMensajes ?? 0) > 0,
  );

  const connectionPacks: ConnectionPack[] = conexionAdicionales.length > 0
    ? conexionAdicionales
        .sort((a, b) => (a.cupoMensajes ?? 0) - (b.cupoMensajes ?? 0))
        .map((item) => ({
          id: `live_wa_${item.id}`,
          quota: item.cupoMensajes ?? 0,
          price: item.precioUnitario,
          enabled: true,
        }))
    : model.connectionPacks;

  const marketingPacks: MarketingPack[] = marketingAdicionales.length > 0
    ? marketingAdicionales
        .sort((a, b) => (a.cupoMensajes ?? 0) - (b.cupoMensajes ?? 0))
        .map((item) => ({
          id: `live_mkt_${item.id}`,
          name: item.nombre,
          emailsPerMonth: item.cupoMensajes ?? 0,
          price: item.precioUnitario,
          enabled: true,
        }))
    : model.marketingPacks;

  const enabledWaPacks = connectionPacks.filter((pack) => pack.enabled);
  const connectionCounts = new Map<string, number>();
  conexionTenants.forEach((tenant) => {
    const limit = usageByTenant.get(String(tenant.id))?.whatsappMonthlyLimit ?? 0;
    const pack = closestPack(limit, enabledWaPacks);
    if (!pack) return;
    connectionCounts.set(pack.id, (connectionCounts.get(pack.id) ?? 0) + 1);
  });

  const enabledMktPacks = marketingPacks.filter((pack) => pack.enabled);
  const marketingCounts = new Map<string, number>();
  marketingTenants.forEach((tenant) => {
    const emails = usageByTenant.get(String(tenant.id))?.emailMessages ?? 0;
    const pack = closestPack(emails > 0 ? emails : (enabledMktPacks[0]?.emailsPerMonth ?? 0), enabledMktPacks.map((item) => ({
      ...item,
      quota: item.emailsPerMonth,
    })));
    if (!pack) return;
    marketingCounts.set(pack.id, (marketingCounts.get(pack.id) ?? 0) + 1);
  });

  let waUsed = 0;
  let waLimit = 0;
  conexionTenants.forEach((tenant) => {
    const usage = usageByTenant.get(String(tenant.id));
    waUsed += usage?.whatsappMessages ?? 0;
    waLimit += usage?.whatsappMonthlyLimit ?? 0;
  });

  let emailUsed = 0;
  let emailQuota = 0;
  marketingTenants.forEach((tenant) => {
    const usage = usageByTenant.get(String(tenant.id));
    emailUsed += usage?.emailMessages ?? 0;
    const pack = closestPack(usage?.emailMessages ?? 0, enabledMktPacks.map((item) => ({
      ...item,
      quota: item.emailsPerMonth,
    })));
    emailQuota += pack?.emailsPerMonth ?? 0;
  });

  const basePrice = priceAt(plans, "BASE", 1, 1)
    ?? cheapestTierPrice(plans, "BASE")
    ?? model.plans.basePrice;
  const avanzadoPrice = priceAt(plans, "AVANZADO", 1, 1)
    ?? cheapestTierPrice(plans, "AVANZADO")
    ?? model.plans.avanzadoPrice;
  const extraFromBase2p = priceAt(plans, "BASE", 2, 1);
  const extraFromAv2p = priceAt(plans, "AVANZADO", 2, 1);
  const extraFromBase2c = priceAt(plans, "BASE", 1, 2);
  const extraFromAv2c = priceAt(plans, "AVANZADO", 1, 2);
  const extraProfessional = extraFromBase2p != null
    ? extraFromBase2p - basePrice
    : extraFromAv2p != null
      ? extraFromAv2p - avanzadoPrice
      : model.plans.extraProfessional;
  const extraConsultorio = extraFromBase2c != null
    ? extraFromBase2c - basePrice
    : extraFromAv2c != null
      ? extraFromAv2c - avanzadoPrice
      : model.plans.extraConsultorio;

  const liveCosts: CostItem[] = snapshot.recurrentes
    .filter((item) => item.activo)
    .map((item) => ({
      id: `live_cost_${item.id}`,
      name: item.nombre,
      amount: asNumber(item.monto),
      currency: "ARS" as const,
      frequency: "monthly" as const,
      category: item.categoria || "Operación",
      kind: "operating" as const,
      enabled: true,
    }));

  const keptCosts = model.costs.filter((item) => !item.id.startsWith("live_cost_"));
  const avgProfessionals = n > 0
    ? clinics.reduce((sum, clinic) => sum + clinic.professionals, 0) / n
    : model.synthetic.avgProfessionals;
  const avgConsultorios = n > 0
    ? clinics.reduce((sum, clinic) => sum + clinic.consultorios, 0) / n
    : model.synthetic.avgConsultorios;

  const pctAvanzado = n > 0 ? avanzadoCount / n : model.adoption.pctAvanzado;
  const pctConexion = n > 0 ? conexionTenants.length / n : model.adoption.pctConexion;
  const pctMarketing = n > 0 ? marketingTenants.length / n : model.adoption.pctMarketing;
  const connectionMix = mixFromCounts(connectionCounts);
  const marketingMix = mixFromCounts(marketingCounts);

  const next: ProjectionModel = {
    ...model,
    plans: {
      basePrice,
      avanzadoPrice: Math.max(avanzadoPrice, basePrice),
      extraProfessional: extraProfessional > 0 ? extraProfessional : model.plans.extraProfessional,
      extraConsultorio: extraConsultorio > 0 ? extraConsultorio : model.plans.extraConsultorio,
    },
    connectionPacks,
    marketingPacks,
    portfolioMode: n > 0 ? "detailed" : model.portfolioMode,
    clinics: n > 0 ? clinics : model.clinics,
    synthetic: {
      clinicCount: n > 0 ? n : model.synthetic.clinicCount,
      avgProfessionals: Math.round(avgProfessionals * 10) / 10,
      avgConsultorios: Math.round(avgConsultorios * 10) / 10,
    },
    costs: liveCosts.length > 0 ? [...keptCosts, ...liveCosts] : model.costs,
    adoption: {
      ...model.adoption,
      pctAvanzado,
      pctConexion,
      pctMarketing,
      connectionMix: connectionMix.length > 0 ? connectionMix : model.adoption.connectionMix,
      marketingMix: marketingMix.length > 0 ? marketingMix : model.adoption.marketingMix,
      whatsappUsageRate: waLimit > 0 ? clamp(waUsed / waLimit, 0, 2) : model.adoption.whatsappUsageRate,
      emailUsageRate: emailQuota > 0 ? clamp(emailUsed / emailQuota, 0, 2) : model.adoption.emailUsageRate,
    },
    growth: {
      ...model.growth,
      newClinicsPerMonth: snapshot.saas
        ? asNumber(snapshot.saas.nuevasMes)
        : model.growth.newClinicsPerMonth,
      churnMonthlyPct: snapshot.saas
        ? clamp(asNumber(snapshot.saas.churnRate) / 100, 0, 1)
        : model.growth.churnMonthlyPct,
    },
  };

  return {
    model: next,
    summary: {
      clinics: n,
      pctAvanzado,
      pctConexion,
      pctMarketing,
      recurrentes: liveCosts.length,
      connectionPacks: connectionPacks.length,
    },
  };
}
